import { FC, useContext, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useRecoilValue } from "recoil";

import {
  DateTimeTemplate,
  DropdownBoundTemplate,
  DropdownTemplate,
} from "@ryde/controls";
import {
  DropdownSourceContext,
  useRideCrudMethods,
  useRideEmptyStationsAllowed,
} from "@ryde/redux";
import {
  dateToComparableNumber,
  DIRECTIONS_CREATE_RIDE,
  editFormDataAF,
  formTabControlsSF,
  IRideDto,
  isPastTaxiSupplierRide,
  isValidDateTime,
  Log,
  requiredRuleStringField,
  RideCrudModeKeyTuple,
  RideEntity,
  RideFormFields,
  RideStationPanelMode,
  useFormats,
  useHasPermissions,
} from "@ryde/shared";

import { FormControlsWrapper } from "../../../../layout-edit";
import { RideSelectDirection } from "./ride-select-direction";
import { RideTabsStationsPanel } from "./ride-tabs-stations-panel";
import { RideTabsWaitingListPanel } from "./ride-tabs-waiting-list-panel";
import { RideBasicTabInputs, RideTabsTabBasicStyled } from "./ride.styled";

export const RideTabsTabBasic: FC = () => {
  const { hasPermissions: hasRideTypeListPermission } = useHasPermissions({
    entity: "rideType",
    action: "list",
  });
  const { hasPermissions: hasRideUpdatePermission } = useHasPermissions({
    entity: "ride",
    action: "update",
  });
  const { dateFormat, timeFormat } = useFormats();
  const rideDto = useRecoilValue<IRideDto | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );
  const { setValue } = useFormContext<RideEntity>();
  const { isValueInDropdownList } = useContext(DropdownSourceContext);
  const basicTabDynamicFields = useRecoilValue(formTabControlsSF("ride"));
  const watchedStartDate = useWatch({ name: RideFormFields.rideStart });
  const watchedEndDate = useWatch({ name: RideFormFields.rideEnd });

  const watchedDirectionValue = useWatch({ name: RideFormFields.direction });
  const watchedRideTypeId = useWatch({ name: RideFormFields.rideTypeId });
  const watchedRideTypeName = useWatch({ name: RideFormFields.rideTypeName });
  const { onRideStartBlurEvent, onRideEndBlurEvent } = useRideCrudMethods();

  // RYDE-7633: a past taxi-supplier ride (GETT/Yango/Kastel/ISR, already started) is locked
  // read-only — every field is disabled; the only permitted actions are Cancel, Delete, and
  // Duplicate to new supplier (enforced in the footer). The backend enforces the same rules.
  const isReadOnlyPastTaxi = useMemo(
    () => isPastTaxiSupplierRide(rideDto),
    [rideDto],
  );

  const rideHasStations = useMemo(() => {
    return !!rideDto?.metadata?.stationIds?.length;
  }, [rideDto?.metadata?.stationIds?.length]);

  const [rideTypeReplaceFieldName, setRideTypeReplacedFieldName] = useState<
    string | undefined
  >(undefined);

  const { isRideHasLockedEndDate } = useRideEmptyStationsAllowed(
    RideStationPanelMode.CRUD_TAB,
    rideDto,
  );

  useEffect(() => {
    const calculateRydeTypeDisplayText = async (): Promise<void> => {
      if (!watchedRideTypeId || !watchedRideTypeName) {
        setRideTypeReplacedFieldName(undefined);
        return;
      }

      const isRydeTypeInList = await isValueInDropdownList(
        "rideType",
        watchedRideTypeId,
      );

      if (!isRydeTypeInList) {
        setRideTypeReplacedFieldName(RideFormFields.rideTypeName);
        return;
      }

      setRideTypeReplacedFieldName(undefined);
    };

    calculateRydeTypeDisplayText();
  }, [isValueInDropdownList, watchedRideTypeId, watchedRideTypeName]);

  useEffect(() => {
    if (
      watchedStartDate &&
      rideDto?.rideStart &&
      isValidDateTime(rideDto.rideStart) &&
      dateToComparableNumber(watchedStartDate) !==
        dateToComparableNumber(rideDto.rideStart)
    ) {
      Log.debug(
        "Update rideStart form value from dto:",
        watchedStartDate,
        "=>",
        rideDto.rideStart,
      );
      setValue(RideFormFields.rideStart, rideDto?.rideStart, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    // we want form to track dto-updates only, not watched form values changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rideDto?.rideStart, setValue]);

  useEffect(() => {
    if (
      watchedEndDate &&
      rideDto?.rideEnd &&
      isValidDateTime(rideDto.rideEnd) &&
      dateToComparableNumber(watchedEndDate) !==
        dateToComparableNumber(rideDto.rideEnd)
    ) {
      Log.debug(
        "Synchronize rideEnd form value from dto:",
        watchedEndDate,
        "=>",
        rideDto.rideEnd,
      );
      setValue(RideFormFields.rideEnd, rideDto.rideEnd, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    // we want form to track dto-updates only, not watched form values changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rideDto?.rideEnd, setValue]);

  return (
    <RideTabsTabBasicStyled>
      <RideBasicTabInputs $twoColumns={hasRideTypeListPermission}>
        {hasRideTypeListPermission && (
          <DropdownBoundTemplate
            name={RideFormFields.rideTypeId}
            formAttributes={{
              fieldNameForm: RideFormFields.rideTypeName,
              dropItemsSource: "rideType",
              dropItemsDisableAdd: true,
              disabled: !hasRideUpdatePermission || isReadOnlyPastTaxi,
              displayTextReplacedByFieldName: rideTypeReplaceFieldName,
              hideDropdownEditButton: true,
            }}
          />
        )}
        <DropdownTemplate
          name={RideFormFields.direction}
          formValidation={requiredRuleStringField}
          formAttributes={{
            selectItems: DIRECTIONS_CREATE_RIDE,
            disabled:
              rideHasStations || !hasRideUpdatePermission || isReadOnlyPastTaxi,
          }}
        />
        <DateTimeTemplate
          name={RideFormFields.rideStart}
          formValidation={requiredRuleStringField}
          onBlur={onRideStartBlurEvent}
          timeFormat={timeFormat}
          dateFormat={dateFormat}
          fullWidth={hasRideTypeListPermission}
          formAttributes={{
            disabled: !hasRideUpdatePermission || isReadOnlyPastTaxi,
          }}
        />
        <DateTimeTemplate
          name={RideFormFields.rideEnd}
          formValidation={requiredRuleStringField}
          onBlur={onRideEndBlurEvent}
          timeFormat={timeFormat}
          dateFormat={dateFormat}
          fullWidth={hasRideTypeListPermission}
          formAttributes={{
            disabled:
              !hasRideUpdatePermission ||
              isRideHasLockedEndDate ||
              isReadOnlyPastTaxi,
          }}
        />
        <FormControlsWrapper
          controls={basicTabDynamicFields}
          disabled={isReadOnlyPastTaxi}
          injectedFormAttributes={{
            customContainerTopMargin: 0,
            hideDropdownEditButton: true,
          }}
        />
      </RideBasicTabInputs>

      {!watchedDirectionValue && <RideSelectDirection />}

      {Boolean(watchedDirectionValue) && <RideTabsStationsPanel />}

      <RideTabsWaitingListPanel />
    </RideTabsTabBasicStyled>
  );
};
